/**
 * Google Translate (and similar in-page translators) rewrite the DOM by
 * wrapping text nodes in <font>/<span> elements outside of React's control.
 * When React later tries to removeChild/insertBefore a node whose parent no
 * longer matches what React expects, the browser throws
 * "Failed to execute 'removeChild' on 'Node'" (Blink) or
 * "The object can not be found here." (WebKit), crashing the app.
 *
 * This guards both DOM methods instead of throwing: removeChild of a node
 * that is no longer a child of `this` becomes a no-op; insertBefore with a
 * detached referenceNode falls back to appendChild, so the node lands at the
 * end of `this` rather than at the intended position until React's next
 * reconciliation pass — a briefly misplaced node beats a crashed page. Each
 * suppressed call is reported (not silently dropped) via the error tracker.
 *
 * Must be imported before React ever touches the DOM (first import in main.tsx).
 */
import { captureError } from './errorTracker';

function report(method: 'removeChild' | 'insertBefore'): void {
  try {
    captureError(new Error(`translateGuard: suppressed ${method} on detached node`), {
      source: 'manual',
      meta: { kind: 'translate-guard' },
    });
  } catch {
    // errorTracker not ready yet — never let reporting crash the guard.
  }
}

const originalRemoveChild = Node.prototype.removeChild;
Node.prototype.removeChild = function <T extends Node>(child: T): T {
  if (child.parentNode !== this) {
    report('removeChild');
    return child;
  }
  return originalRemoveChild.call(this, child) as T;
};

const originalInsertBefore = Node.prototype.insertBefore;
Node.prototype.insertBefore = function <T extends Node>(newNode: T, referenceNode: Node | null): T {
  if (referenceNode && referenceNode.parentNode !== this) {
    report('insertBefore');
    return this.appendChild(newNode) as T;
  }
  return originalInsertBefore.call(this, newNode, referenceNode) as T;
};
